/**
 * Compliance Horizon — a regulatory-date timeline for the applicable rules
 * (ADR-0007, Phase 2).
 *
 * Honesty contract:
 *   - Renders ONLY dates that genuinely exist in a rule's `temporal` fields.
 *     Nothing is inferred, computed, or extrapolated (no "deadline in N days").
 *   - Every date is advisory and carries the rule's source link (citation) and
 *     lifecycle maturity. Dates on DRAFT / SEED_UNVERIFIED rules are themselves
 *     flagged unverified.
 *   - It is not a legal determination; a human verifies against the official
 *     source (ADR-0002).
 *
 * Data path: a pure client-side join of `GET /v1/seed-rules` (real temporal
 * data) to the applicable card ids from the current evaluation — no schema
 * change, no evaluator change.
 */

import type { ComplianceRequirementCard } from "@regpulse/contracts";
import type { SeedRule, SeedRuleTemporal } from "../services/api";

const DATE_FIELDS: Array<{ key: keyof SeedRuleTemporal; label: string }> = [
  { key: "entry_into_force", label: "Entry into force" },
  { key: "applies_to_new_types_from", label: "Applies · new types" },
  { key: "applies_to_all_new_vehicles_from", label: "Applies · all new vehicles" },
  { key: "applies_to_first_registration_from", label: "Applies · first registration" },
  { key: "applies_from_generic", label: "Applies from" },
  { key: "small_volume_derogation_until", label: "Small-volume derogation until" },
  { key: "effective_to", label: "Effective until" },
];

const VERIFIED_STATES = new Set(["ACTIVE"]);

interface HorizonEntry {
  ruleId: string;
  title: string;
  date: string;
  label: string;
  sourceUrl: string | null;
  lifecycleState: string;
  layer: string;
}

interface Props {
  rules: SeedRule[];
  cards: ComplianceRequirementCard[];
}

export function ComplianceHorizon({ rules, cards }: Props) {
  const applicableCards = cards.filter((c) => c.applicabilityCategory === "applicable");
  const applicableIds = new Set(applicableCards.map((c) => c.id));
  const layerById = new Map<string, string>(applicableCards.map((c) => [c.id, c.layer]));
  const ruleById = new Map(rules.map((r) => [r.stable_id, r]));

  const entries: HorizonEntry[] = [];
  for (const id of applicableIds) {
    const rule = ruleById.get(id);
    if (!rule || !rule.temporal) continue;
    const sourceUrl = rule.sources?.find((s) => s.official_url)?.official_url ?? null;
    for (const f of DATE_FIELDS) {
      const v = rule.temporal[f.key];
      if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) {
        entries.push({
          ruleId: rule.stable_id,
          title: rule.title,
          date: v.slice(0, 10),
          label: f.label,
          sourceUrl,
          lifecycleState: rule.lifecycle_state ?? "UNKNOWN",
          layer: layerById.get(id) ?? "?",
        });
      }
    }
  }
  // A rule often repeats the SAME date across several temporal fields
  // (entry-into-force = applies-from = first-registration). Merge those into
  // one row listing the date kinds, so the timeline is not noisy.
  const mergedMap = new Map<string, HorizonEntry>();
  for (const e of entries) {
    const k = `${e.ruleId}|${e.date}`;
    const prev = mergedMap.get(k);
    if (prev) {
      if (!prev.label.split(" / ").includes(e.label)) prev.label = `${prev.label} / ${e.label}`;
    } else {
      mergedMap.set(k, { ...e });
    }
  }
  // Upcoming first: a horizon leads with the nearest / future deadlines; the
  // long-standing in-force dates (some reaching back decades) sort to the
  // bottom as the regulatory baseline.
  const deduped = Array.from(mergedMap.values()).sort((a, b) =>
    a.date < b.date ? 1 : a.date > b.date ? -1 : 0,
  );

  const byYear = new Map<string, HorizonEntry[]>();
  for (const e of deduped) {
    const y = e.date.slice(0, 4);
    if (!byYear.has(y)) byYear.set(y, []);
    byYear.get(y)!.push(e);
  }
  const years = Array.from(byYear.keys()).sort().reverse();

  return (
    <section
      data-testid="compliance-horizon"
      aria-labelledby="compliance-horizon-title"
      style={{
        background: "var(--color-surface-elevated)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-8)",
        padding: "var(--space-12)",
      }}
    >
      <header style={{ marginBottom: "var(--space-8)" }}>
        <h3 id="compliance-horizon-title" className="type-h3" style={{ margin: 0, fontSize: 13 }}>
          Compliance Horizon ·{" "}
          <span style={{ color: "var(--color-text-secondary)", fontWeight: 400 }}>
            regulatory dates for applicable rules
          </span>
        </h3>
        <p
          className="type-caption"
          style={{ margin: "var(--space-4) 0 0 0", color: "var(--color-text-secondary)" }}
        >
          Advisory only. Dates are read verbatim from the registry's temporal fields — none are
          inferred or computed. Each is tied to its official source; dates on DRAFT /
          SEED_UNVERIFIED rules are themselves unverified. Not a legal determination — verify
          against the cited source (ADR-0002).
        </p>
        <p
          data-testid="horizon-legend"
          className="type-caption"
          style={{ margin: "var(--space-4) 0 0 0", color: "var(--color-text-secondary)", fontSize: 10 }}
        >
          Date kinds — <strong>Entry into force</strong>: when the instrument took legal effect
          (often historical — the in-force baseline). <strong>Applies · new types / first
          registration / all new vehicles</strong>: when the requirement starts applying to those
          vehicles. <strong>Applies from</strong>: generic start. <strong>Effective until /
          derogation until</strong>: end / exemption windows. Upcoming dates lead; long-standing
          in-force dates sort to the bottom.
        </p>
      </header>

      {entries.length === 0 ? (
        <p
          className="type-caption"
          data-testid="horizon-empty"
          style={{ margin: 0, color: "var(--color-text-disabled)", fontStyle: "italic" }}
        >
          No dated regulatory milestones recorded in the registry for the applicable set.
        </p>
      ) : (
        <>
          <GanttTimeline entries={deduped} />
          <div style={{ maxHeight: 360, overflowY: "auto", display: "grid", gap: "var(--space-8)", marginTop: "var(--space-8)" }}>
          {years.map((y) => {
            const rows = byYear.get(y)!;
            return (
              <div key={y} data-testid={`horizon-year-${y}`}>
                <div
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "var(--color-surface-elevated)",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--color-text-secondary)",
                    padding: "2px 0",
                    borderBottom: "1px solid var(--color-border)",
                  }}
                >
                  {y} · {rows.length}
                </div>
                <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                  {rows.map((e) => {
                    const unverified = !VERIFIED_STATES.has(e.lifecycleState);
                    return (
                      <li
                        key={`${e.ruleId}-${e.date}-${e.label}`}
                        data-testid="horizon-entry"
                        data-rule={e.ruleId}
                        data-date={e.date}
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "baseline",
                          gap: "var(--space-8)",
                          padding: "var(--space-4) 0",
                          fontSize: 12,
                          borderBottom: "1px solid var(--color-border)",
                        }}
                      >
                        <span className="type-mono" style={{ fontSize: 11, minWidth: 84 }}>
                          {e.date}
                        </span>
                        <span style={{ color: "var(--color-text-secondary)", minWidth: 140 }}>
                          {e.label}
                        </span>
                        <span style={{ minWidth: 0, flex: 1 }}>
                          {e.title}{" "}
                          <code className="type-mono" style={{ fontSize: 10, color: "var(--color-text-secondary)" }}>
                            ({e.ruleId})
                          </code>
                        </span>
                        {unverified && (
                          <span
                            data-testid="horizon-unverified"
                            title={`Rule lifecycle: ${e.lifecycleState}`}
                            style={{
                              fontSize: 10,
                              padding: "0 var(--space-4)",
                              borderRadius: "var(--radius-4)",
                              border: "1px dashed var(--status-candidate-pending)",
                              color: "var(--status-candidate-pending)",
                            }}
                          >
                            unverified
                          </span>
                        )}
                        {e.sourceUrl ? (
                          <a
                            href={e.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 11, color: "var(--color-accent)" }}
                          >
                            source ↗
                          </a>
                        ) : (
                          <span style={{ fontSize: 10, color: "var(--color-text-disabled)" }}>
                            no source link
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
          </div>
        </>
      )}
    </section>
  );
}

function GanttTimeline({ entries }: { entries: HorizonEntry[] }) {
  const toNum = (d: string) => {
    const [y, m] = d.split("-");
    return Number(y) + (Number(m) - 1) / 12;
  };
  const nums = entries.map((e) => toNum(e.date));
  const min = Math.floor(Math.min(...nums));
  const max = Math.max(min + 1, Math.ceil(Math.max(...nums)));
  // Focus the axis on the most recent ~18 years so the dense upcoming cluster
  // has room to breathe; older in-force dates compress against the left edge
  // (and remain in full in the detail list below).
  const windowStart = Math.max(min, max - 18);
  const span = Math.max(1, max - windowStart);
  const pct = (num: number) => Math.max(0, Math.min(100, ((num - windowStart) / span) * 100));
  const order = ["A", "B", "C", "D", "P", "U"];
  const lanes = order.filter((l) => entries.some((e) => e.layer === l));
  // Thin out year labels so the axis is not a wall of tiny numbers (~10 max).
  const labelStep = Math.max(1, Math.ceil((max - windowStart) / 10));
  const tickYears: number[] = [];
  for (let y = windowStart; y <= max; y += labelStep) tickYears.push(y);
  const xPct = (d: string) => pct(toNum(d));
  const now = new Date();
  const todayNum = now.getFullYear() + now.getMonth() / 12;
  const todayInRange = todayNum >= windowStart && todayNum <= max;
  const todayPct = pct(todayNum);
  const upcomingCount = entries.filter((e) => toNum(e.date) >= todayNum).length;
  const earlierCount = entries.filter((e) => toNum(e.date) < windowStart).length;

  return (
    <div data-testid="horizon-gantt" style={{ marginBottom: "var(--space-8)" }}>
      <div style={{ position: "relative", height: 14, marginLeft: 20 }}>
        {tickYears.map((y) => (
          <span
            key={y}
            className="type-mono"
            style={{
              position: "absolute",
              left: `${pct(y)}%`,
              fontSize: 9,
              color: "var(--color-text-secondary)",
              transform: "translateX(-50%)",
            }}
          >
            {y}
          </span>
        ))}
        {todayInRange && (
          <span
            data-testid="gantt-today-label"
            style={{ position: "absolute", left: `${todayPct}%`, fontSize: 9, fontWeight: 700, color: "var(--color-accent)", transform: "translateX(-50%)" }}
          >
            today
          </span>
        )}
      </div>
      <div style={{ display: "grid", gap: 3 }}>
        {lanes.map((l) => (
          <div
            key={l}
            data-testid={`gantt-lane-${l}`}
            style={{ display: "grid", gridTemplateColumns: "16px 1fr", alignItems: "center", gap: 4 }}
          >
            <span style={{ fontSize: 10, fontWeight: 700 }}>{l}</span>
            <div
              style={{
                position: "relative",
                height: 14,
                background: "var(--color-surface-sunken)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-4)",
              }}
            >
              {tickYears.map((y) => (
                <div
                  key={y}
                  aria-hidden
                  style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: `${pct(y)}%`,
                    width: 1,
                    background: "var(--color-border)",
                  }}
                />
              ))}
              {todayInRange && (
                <div
                  data-testid="gantt-today"
                  aria-hidden
                  style={{ position: "absolute", top: -1, bottom: -1, left: `${todayPct}%`, width: 2, background: "var(--color-accent)" }}
                />
              )}
              {entries
                .filter((e) => e.layer === l)
                .map((e) => {
                  const upcoming = toNum(e.date) >= todayNum;
                  const size = upcoming ? 10 : 7;
                  return (
                    <span
                      key={`${e.ruleId}-${e.date}-${e.label}`}
                      data-when={upcoming ? "upcoming" : "past"}
                      title={`${e.date} · ${e.label} · ${e.title} (${e.ruleId})${upcoming ? " · upcoming" : " · in force"}`}
                      style={{
                        position: "absolute",
                        top: `calc(50% - ${size / 2}px)`,
                        left: `calc(${xPct(e.date)}% - ${size / 2}px)`,
                        width: size,
                        height: size,
                        borderRadius: "50%",
                        background: VERIFIED_STATES.has(e.lifecycleState)
                          ? "var(--color-accent)"
                          : "var(--status-candidate-pending)",
                        border: "1px solid var(--color-surface-elevated)",
                        opacity: upcoming ? 1 : 0.55,
                        boxShadow: upcoming
                          ? "0 0 0 2px color-mix(in srgb, var(--color-accent) 30%, transparent)"
                          : "none",
                      }}
                    />
                  );
                })}
            </div>
          </div>
        ))}
      </div>
      <p className="type-caption" style={{ margin: "4px 0 0 0", color: "var(--color-text-secondary)", fontSize: 9 }}>
        Window {windowStart}–{max}; the accent line marks today — solid + haloed = upcoming
        ({upcomingCount}), faded = in-force baseline
        {earlierCount > 0 ? ` (${earlierCount} earlier date(s) compressed at the left edge; full list below)` : ""};
        amber fill = unverified rule lifecycle. Lanes are ABCDPU.
      </p>
    </div>
  );
}
